// src/pages/utilities/UtilityFormPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import DocumentManager from "../../components/documents/DocumentManager";

import {
  createUtility,
  updateUtility,
  getUtilityParentDetails,
} from "../../api/utilityApi";
import { getPropertyById } from "../../api/unitApi";

import {
  Box,
  Paper,
  TextField,
  Typography,
  Grid,
  Alert,
  Snackbar,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";

function UtilityFormPage() {
  const { propertyId, unitId, utilityId } = useParams();
  const navigate = useNavigate();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();

  const isEdit = Boolean(utilityId);
  const isUnitContext = Boolean(unitId);
  const parentId = isUnitContext ? unitId : propertyId;

  // Determine the back path based on context
  const backPath = isUnitContext
    ? `/units/${unitId}/utilities`
    : `/properties/${propertyId}/utilities`;
  const goBack = useBackNavigation(backPath);

  const UTILITY_TYPES = ["Gas", "Electricity", "Water", "Sewage"];

  const [form, setForm] = useState({
    utility_type: "",
    supplier_name: "",
    email_address: "",
    contact_number: "",
    meter_number: "",
  });
  const [resolvedPropertyId, setResolvedPropertyId] = useState(propertyId || "");

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load breadcrumbs
  useEffect(() => {
    const loadBreadcrumbs = async () => {
      try {
        const data = await getUtilityParentDetails(propertyId, unitId);
        const actionLabel = isEdit ? "Edit" : "Create";

        if (isUnitContext) {
          const propertyData = await getPropertyById(data.unit.property_id);
          setResolvedPropertyId(data.unit.property_id);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: propertyData.name, path: `/properties/${data.unit.property_id}/details` },
            {
              label: `Unit ${data.unit.unit_name || data.unit.unit_code || data.unit.id}`,
              path: `/units/${unitId}/details`,
            },
            { label: "Utilities", path: `/units/${unitId}/utilities` },
            { label: actionLabel, path: null },
          ]);
        } else {
          setResolvedPropertyId(propertyId);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: data.name, path: `/properties/${propertyId}/details` },
            { label: "Utilities", path: `/properties/${propertyId}/utilities` },
            { label: actionLabel, path: null },
          ]);
        }
      } catch (err) {
        console.error("Failed to load breadcrumbs:", err);
      }
    };

    loadBreadcrumbs();
    return () => clearCustomPath();
  }, [propertyId, unitId, isUnitContext, isEdit, setCustomPath, clearCustomPath]);

  // LOAD EXISTING UTILITY
  useEffect(() => {
    if (!isEdit) return;

    const loadUtility = async () => {
      try {
        const data = await getUtilityParentDetails(propertyId, unitId);
        const utility = data.utilities?.find(
          (u) => u.id === parseInt(utilityId)
        );

        if (utility) {
          setForm({
            utility_type: utility.utility_type || "",
            supplier_name: utility.supplier_name || "",
            email_address: utility.email_address || "",
            contact_number: utility.contact_number || "",
            meter_number: utility.meter_number || "",
          });
        }
      } catch (err) {
        setError("Failed to load utility details.");
      }
    };

    loadUtility();
  }, [isEdit, propertyId, unitId, utilityId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      ...form,
      ...(isUnitContext ? { unit_id: parentId } : { property_id: parentId }),
    };

    try {
      if (isEdit) {
        await updateUtility(utilityId, payload);
      } else {
        await createUtility(payload);
      }

      navigate(
        isUnitContext
          ? `/units/${unitId}/utilities`
          : `/properties/${propertyId}/utilities`,
        {
          state: {
            toast: {
              message: isEdit ? "Utility updated" : "Utility created",
              severity: "success",
            },
          },
        }
      );
    } catch {
      setToast({
        open: true,
        message: "Failed to save utility. Please try again.",
        severity: "error",
      });
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Box className="page-container">
        <Paper sx={{ p: 4, width: "100%" }} elevation={2}>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
            {isEdit ? "Edit Utility" : "Add New Utility"}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  required
                  size="large"
                  sx={{ minHeight: 64, minWidth: 260 }}
                >
                  <InputLabel id="utility-type-label" sx={{ fontSize: "1rem" }}>Utility Type</InputLabel>
                  <Select
                    labelId="utility-type-label"
                    label="Utility Type"
                    name="utility_type"
                    value={form.utility_type}
                    onChange={handleChange}
                    sx={{ 
                      minHeight: 64,
                      fontSize: "1.1rem",
                      "& .MuiSelect-select": {
                        padding: "16px 14px",
                        fontSize: "1.1rem"
                      }
                    }}
                  >
                    {UTILITY_TYPES.map((type) => (
                      <MenuItem key={type} value={type} sx={{ fontSize: "1.1rem", py: 1.5 }}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Supplier Name"
                  name="supplier_name"
                  fullWidth
                  required
                  value={form.supplier_name}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Email Address"
                  name="email_address"
                  type="email"
                  fullWidth
                  value={form.email_address}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Contact Number"
                  name="contact_number"
                  fullWidth
                  value={form.contact_number}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Meter Number"
                  name="meter_number"
                  fullWidth
                  value={form.meter_number}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: "flex", gap: 2, mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  size="large"
                  startIcon={
                    saving ? <CircularProgress size={18} color="inherit" /> : null
                  }
                  sx={{ minWidth: 150 }}
                >
                  {isEdit ? "Update Utility" : "Create Utility"}
                </Button>

                <Button
                  variant="outlined"
                  disabled={saving}
                  onClick={() => goBack()}
                  size="large"
                  sx={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <DocumentManager
          propertyId={resolvedPropertyId}
          targetType="utility"
          targetId={utilityId}
        />
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default UtilityFormPage;
